import styled from 'styled-components';
import { InputGroup } from 'react-bootstrap';
import { COLORS } from 'modules/core/theme/colors';
import { CSSProperties } from 'react';

const Prepend = InputGroup.Text;

export type InputFontWeight = 'normal' | 'bold';

export const Input = styled(InputGroup)<{
  textstyle: InputFontWeight;
  $isInvalid: boolean;
  $endAdornment: boolean;
  $startAdornment: boolean;
  $isClickCallback?: boolean;
  $readonly?: boolean;
  $placeholderColor?: CSSProperties | undefined;
  $isAnimated?: boolean;
  $isDisabled?: boolean;
}>`
  border-radius: 0.375rem;
  box-shadow: ${({ $isAnimated }) => $isAnimated && `0px 0px 3px 3px ${COLORS.BLUE_12}a1`};
  border-color: ${({ $isAnimated }) => $isAnimated && COLORS.BLUE_12};
  border: 1px solid ${({ $isInvalid }) => ($isInvalid ? COLORS.RED_8 : COLORS.GREY_32)};
  box-sizing: border-box;

  &:focus-within {
    box-shadow: 0px 0px 4px 1px ${({ $isInvalid }) => ($isInvalid ? COLORS.RED_8 : 'rgba(44, 102, 185, 1)')};
    border: 1px solid ${({ $isInvalid }) => ($isInvalid ? COLORS.RED_8 : 'rgba(44, 102, 185, 1)')} !important;
  }

  span {
    border: 0;
    margin-left: 0 !important;
  }

  input {
    margin-left: 0 !important;
    padding: 0.375rem 6px;
    border: 0 !important;
    height: ${(props) => props.theme.variables.inputHeight};
    font-size: ${(props) => props.theme.fontSize.normal};
    font-family: ${(props) => props.theme.fontFamily.roboto};
    text-align: ${(props) => props.textposition};
    color: ${({ $isAnimated, $isDisabled, theme }) =>
      $isAnimated ? `transparent` : $isDisabled ? `${COLORS.GREY_33}` : theme.colors.black};
    transition: color 0.5s ease;
    cursor: ${({ $isClickCallback }) => $isClickCallback && `pointer`};
    font-weight: ${(props) => {
      const mapFontWeight = {
        normal: '',
        bold: props.theme.boldText.bold,
      };

      return mapFontWeight[props.textstyle];
    }};

    &:focus {
      outline: 0;
      box-shadow: unset !important;
    }

    &:disabled {
      background-color: ${COLORS.GREY_31};
    }

    ${({ $readonly }) =>
      $readonly &&
      `

        &:focus {
          outline: 0;
          box-shadow: unset;
          border-color: ${COLORS.GREY_32};
        }
      `}

    &::placeholder {
      ${({ $placeholderStyles }) => $placeholderStyles}
      color: ${({ $isAnimated }) => $isAnimated && `transparent`};
      transition: color 0.5s ease;
    }
  }

  textarea {
    resize: none;
    height: 150px;
  }

  input,
  textarea {
    border: ${(props) => props.$isInvalid && `1px solid ${COLORS.RED_3}`};
  }

  ${({ $endAdornment, $startAdornment, $isDisabled, $isInvalid, theme }) => {
    const radiusStyle = '0.375rem !important;';
    let style = '';

    if ($startAdornment) {
      style += `
        span:nth-child(1).input-group-text {
          color: ${$isDisabled ? COLORS.GREY_33 : theme.colors.black};
          background-color: ${$isDisabled ? COLORS.GREY_31 : `inherit`};
          border-color: ${$isInvalid ? COLORS.RED_6 : ''};
          border-top-left-radius: ${radiusStyle};
          border-bottom-left-radius: ${radiusStyle};
          padding: 0 5px 0 5px;
        };

        input {
          padding-left: 0 !important;
        }
      `;
    }

    if ($endAdornment) {
      style += `
        span:nth-last-child(1).input-group-text {
          color: ${$isDisabled ? COLORS.GREY_33 : theme.colors.black};
          background-color: ${$isDisabled ? COLORS.GREY_31 : `inherit`};
          border-color: ${$isInvalid ? COLORS.RED_6 : ''};
          border-top-right-radius: ${radiusStyle}
          border-bottom-right-radius: ${radiusStyle}
          padding: 0 5px 0 5px;
        };

        span:nth-last-child(1).input-group-text img {
          ${$isDisabled ? `opacity: 0.5; filter: grayscale(100%); cursor: default !important;` : ''}
        }

        input {
          padding-right: 0 !important;
        }
      `;
    }

    style += `input { border-radius: ${!$startAdornment && !$endAdornment ? radiusStyle : 'unset !important;'} }`;

    if ($startAdornment && !$endAdornment) {
      style += `
        input {
          border-top-right-radius: ${radiusStyle}
          border-bottom-right-radius: ${radiusStyle}
        }
      `;
    }

    if (!$startAdornment && $endAdornment) {
      style += `
        input {
          border-top-left-radius: ${radiusStyle}
          border-bottom-left-radius: ${radiusStyle}
        }
      `;
    }

    return style;
  }}

  .is-invalid {
    background-image: none;
    padding-right: 12px;
  }

  .invalid-feedback {
    display: block !important;
    font-size: 12px;
    margin-top: 0;
    div {
      margin: 3px 0;
    }
  }

  &.multiselect {
    border: none !important;
    box-shadow: none !important;
  }

  & .react-datepicker-wrapper {
    flex: 1;
  }
`;

export const Adornment = styled(Prepend)<{ $isCursorPointer?: boolean }>`
  background: white;
  height: ${(props) => props.theme.variables.inputHeight};
  display: flex;
  justify-content: center;
  cursor: ${({ $isCursorPointer }) => $isCursorPointer && 'pointer'};
  padding: 0;
`;
